import { Fragment, useEffect, useMemo, useState, type ReactNode } from 'react'
import { ArrowLeft, ChevronDown, ChevronRight, FileText } from 'lucide-react'
import { preisText, vergleichspreis } from '@symphenia/shared-types'
import { apiErrorText } from '@/lib/api-error'
import { Abschnitt } from '@/lib/speichern'
import { useTerminology } from '@/lib/useTerminology'
import { confirmPairings, fetchOpenPositions, patchIncomingLine, setLineSource, suggestPairings } from '../api/incoming-invoices.api'
import { abgleichStand, bestellGruppen, imAbgleich, kandidaten, ohneAbweichung, suchePositionen, worinPasst } from '../lib/abgleich'
import { ABWEICHUNG_TEXT, mengeText, tagText } from '../lib/texte'
import type { IncomingInvoiceDetail, IncomingInvoiceLine, OpenPosition } from '../types/incoming-invoices.types'

const knopf = 'rounded-md border border-border px-2.5 py-1 text-sm hover:bg-muted disabled:opacity-50'
const klein = 'rounded border border-border px-2 py-0.5 text-xs hover:bg-muted disabled:opacity-50'
const etikett = 'inline-flex items-center rounded-full px-2 py-0.5 text-xs font-medium'
const gelb = 'bg-amber-500/15 text-amber-700 dark:text-amber-300'
const blau = 'bg-sky-500/15 text-sky-700 dark:text-sky-300'
const gruen = 'bg-emerald-500/15 text-emerald-700 dark:text-emerald-300'
const auffaellig = 'rounded bg-amber-500/20 px-1 font-medium text-amber-800 dark:text-amber-200'

/** Höchstens so viele Treffer der freien Suche auf einmal — wer mehr braucht, tippt ein Wort dazu. */
const SUCHE_HOECHSTENS = 12

/**
 * AP-34 Nachtrag 3 (PT-9, PT-10; E-6 bis E-9) — **der Abgleich als eigene Ansicht** über die volle Breite.
 *
 * Je Zeile eine Rechnungsposition und daneben ihre Bestellposition; sortiert nach dem, was zu tun ist:
 * zu klären · zu bestätigen · erledigt (je Bestellung eingeklappt, mit dem, was von ihr offen bleibt).
 * Vorher standen Auswahl, Bestelltabellen und Erfassungszeilen untereinander — bei zwei Positionen
 * ging das, bei einer Sammelrechnung nicht.
 *
 * Alles hier ist eine Handlung mit Knopf (`speichern.md`, Fall 3): zuordnen, bestätigen, lösen,
 * „ohne Bezug", „Nebenkosten". Bearbeitet werden die Positionen weiter im Erfassungsraster.
 */
export function Abgleich({
  rechnung,
  onRechnung,
  onFehler,
  onSchliessen,
  originalSichtbar,
  onOriginal,
}: {
  rechnung: IncomingInvoiceDetail
  onRechnung: (r: IncomingInvoiceDetail) => void
  onFehler: (t: string | null) => void
  onSchliessen: () => void
  /** E-7: Das Original klappt im Abgleich zur Seite und kommt mit einem Klick auf die Zeile zurück. */
  originalSichtbar: boolean
  onOriginal: (sichtbar: boolean) => void
}) {
  const terms = useTerminology()
  const [positionen, setPositionen] = useState<OpenPosition[] | null>(null)
  const [laeuft, setLaeuft] = useState(false)
  /** die Zeile, deren Auswahl offen ist */
  const [wahl, setWahl] = useState<string | null>(null)
  const [suche, setSuche] = useState('')
  const [aufgeklappt, setAufgeklappt] = useState<Set<string>>(new Set())

  // Neu laden, sobald sich an Lieferant, Art oder den Bezügen und Mengen der Zeilen etwas ändert
  const stand = useMemo(() => [rechnung.supplierId, rechnung.kind, ...rechnung.lines.map((z) => `${z.id}:${z.sourceLineId ?? ''}:${z.quantity}`)].join('|'), [rechnung.supplierId, rechnung.kind, rechnung.lines])
  useEffect(() => {
    let aus = false
    fetchOpenPositions(rechnung.id)
      .then((p) => !aus && setPositionen(p))
      .catch(() => !aus && setPositionen([]))
    return () => {
      aus = true
    }
  }, [rechnung.id, stand])

  const tue = async (was: () => Promise<IncomingInvoiceDetail>, fehlertext: string) => {
    try {
      setLaeuft(true)
      onFehler(null)
      onRechnung(await was())
      return true
    } catch (e) {
      onFehler(apiErrorText(e, fehlertext))
      return false
    } finally {
      setLaeuft(false)
    }
  }

  const liste = useMemo(() => positionen ?? [], [positionen])
  const jePosition = useMemo(() => new Map(liste.map((p) => [p.quelleId, p])), [liste])
  const zeilen = useMemo(() => rechnung.lines.filter(imAbgleich), [rechnung.lines])
  const offen = zeilen.filter((z) => abgleichStand(z) === 'OFFEN')
  const zuPruefen = zeilen.filter((z) => abgleichStand(z) === 'PRUEFEN')
  const erledigt = zeilen.filter((z) => abgleichStand(z) === 'ERLEDIGT')
  const glatt = zuPruefen.filter(ohneAbweichung)
  const gruppen = useMemo(() => bestellGruppen(liste), [liste])
  const ohneBestellung = erledigt.filter((z) => !z.sourceLineId)
  /** Zugeordnet, aber die Bestellposition gibt es nicht mehr — die Zeile darf deshalb nicht aus der Liste fallen. */
  const verwaist = erledigt.filter((z) => z.sourceLineId && !jePosition.has(z.sourceLineId))
  const bearbeitbar = rechnung.editable

  const zuordnen = async (z: IncomingInvoiceLine, quelleId: string) => {
    if (await tue(() => setLineSource(rechnung.id, z.id, quelleId, z.updatedAt), 'Die Zuordnung ließ sich nicht setzen.')) {
      setWahl(null)
      setSuche('')
    }
  }
  const loesen = (z: IncomingInvoiceLine) => tue(() => setLineSource(rechnung.id, z.id, null, z.updatedAt), 'Der Bezug ließ sich nicht lösen.')
  const ohneBezug = (z: IncomingInvoiceLine, ja: boolean) => tue(() => setLineSource(rechnung.id, z.id, null, z.updatedAt, ja), 'Das Kennzeichen ließ sich nicht setzen.')
  const alsArt = (z: IncomingInvoiceLine, lineType: 'CHARGE' | 'ITEM') => tue(() => patchIncomingLine(rechnung.id, z.id, { lineType }, z.updatedAt), 'Die Art der Position ließ sich nicht ändern.')
  const bestaetigen = (ids: string[]) => tue(() => confirmPairings(rechnung.id, ids), 'Die Zuordnung ließ sich nicht bestätigen.')
  const vorschlagen = () => tue(async () => (await suggestPairings(rechnung.id)).data, 'Die Zuordnung ließ sich nicht vorschlagen.')

  const klappe = (id: string) =>
    setAufgeklappt((alt) => {
      const neu = new Set(alt)
      if (neu.has(id)) neu.delete(id)
      else neu.add(id)
      return neu
    })

  /** Die Seite der Rechnung: Ein Klick darauf holt das Original zurück (E-7). */
  const rechnungsZellen = (z: IncomingInvoiceLine) => {
    const menge = z.deviations.includes('QUANTITY_EXCEEDS_OPEN')
    const einheit = z.deviations.includes('UNIT_MISMATCH')
    // „Preis niedriger" ist nur ein Hinweis (oft ein Rundungsrest) — hervorgehoben wird, was jemand ansehen muss
    const preis = z.deviations.includes('PRICE_ABOVE_TOLERANCE')
    return (
      <>
        <td className="w-10 px-2 py-1.5 text-right tabular-nums text-muted-foreground">{z.positionNo}</td>
        <td className="cursor-pointer px-2 py-1.5" title={originalSichtbar ? undefined : 'Klick zeigt das Original'} onClick={() => !originalSichtbar && onOriginal(true)}>
          <span className="line-clamp-2">{z.name}</span>
        </td>
        <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums">
          <span className={menge ? auffaellig : ''}>{mengeText(z.quantity)}</span> <span className={einheit ? auffaellig : 'text-muted-foreground'}>{z.unitCode ?? ''}</span>
        </td>
        <td className="whitespace-nowrap border-r border-border px-2 py-1.5 text-right tabular-nums">
          <span className={preis ? auffaellig : ''}>{preisText(z.unitPrice)}</span>
          {z.priceUnit !== 1 && <span className="text-xs text-muted-foreground"> / {z.priceUnit}</span>}
          {!!z.discountPercent && <span className="text-xs text-muted-foreground"> −{mengeText(z.discountPercent)} %</span>}
        </td>
      </>
    )
  }

  /** Die Seite der Bestellung zu einer zugeordneten Zeile — mit der offenen Menge **vor** dieser Rechnung. */
  const bestellZellen = (z: IncomingInvoiceLine) => {
    const p = z.sourceLineId ? jePosition.get(z.sourceLineId) : undefined
    const menge = z.deviations.includes('QUANTITY_EXCEEDS_OPEN')
    const einheit = z.deviations.includes('UNIT_MISMATCH')
    const preis = z.deviations.includes('PRICE_ABOVE_TOLERANCE')
    const soll = p ? vergleichspreis(p) : null
    return (
      <>
        <td className="px-2 py-1.5">
          <span className="line-clamp-2">{p?.bezeichnung ?? <span className="text-muted-foreground">Bestellung {z.sourceDocumentNumber ?? ''}</span>}</span>
        </td>
        <td className="whitespace-nowrap px-2 py-1.5 text-muted-foreground">{p ? `${p.bestellnummer}/${p.bestellzeileNr}` : (z.sourceDocumentNumber ?? '')}</td>
        <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums">
          <span className={menge ? auffaellig : ''}>{z.sourceOpenQuantityAtMatch !== null ? mengeText(z.sourceOpenQuantityAtMatch) : p ? mengeText(Number(p.bestellteMenge)) : ''}</span>{' '}
          <span className={einheit ? auffaellig : 'text-muted-foreground'}>{p?.unitCode ?? ''}</span>
        </td>
        <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums">
          <span className={preis ? auffaellig : ''}>{preisText(z.sourceUnitPrice ?? soll?.einzelpreis ?? null)}</span>
          {(z.sourcePriceUnit ?? 1) !== 1 && <span className="text-xs text-muted-foreground"> / {z.sourcePriceUnit}</span>}
          {!!z.sourceDiscountPercent && <span className="text-xs text-muted-foreground"> −{mengeText(z.sourceDiscountPercent)} %</span>}
        </td>
      </>
    )
  }

  const abweichungen = (z: IncomingInvoiceLine) =>
    z.deviations
      .filter((d) => d !== 'NO_SOURCE')
      .map((d) => (
        <span key={d} className={`mr-1 rounded px-1 text-xs ${ABWEICHUNG_TEXT[d]?.klasse ?? 'bg-muted'}`}>
          {ABWEICHUNG_TEXT[d]?.label ?? d}
        </span>
      ))

  /** Ein Kandidat als Knopf: die Position, worin sie passt, und die Handlung beim Namen. */
  const kandidatKnopf = (z: IncomingInvoiceLine, p: OpenPosition, passt: string[]) => {
    const soll = vergleichspreis(p)
    return (
      <li key={p.quelleId} className="flex flex-wrap items-center gap-x-3 gap-y-1 rounded-md border border-border bg-background px-2.5 py-1.5">
        <span className="whitespace-nowrap font-medium">
          {p.bestellnummer}/{p.bestellzeileNr}
        </span>
        <span className="min-w-0 flex-1">{p.bezeichnung}</span>
        <span className="whitespace-nowrap tabular-nums text-muted-foreground">
          offen {mengeText(p.offen)} {p.unitCode ?? ''} · {preisText(soll.einzelpreis)}
        </span>
        {passt.length > 0 && <span className={`${etikett} ${gruen}`}>passt: {passt.join(', ')}</span>}
        <button type="button" className="rounded-md bg-primary px-2.5 py-1 text-xs font-medium text-primary-foreground hover:opacity-90 disabled:opacity-50" disabled={laeuft} onClick={() => void zuordnen(z, p.quelleId)}>
          Zuordnen
        </button>
      </li>
    )
  }

  const auswahl = (z: IncomingInvoiceLine) => {
    const vorschlaege = kandidaten(z, liste)
    const treffer = suche.trim() ? suchePositionen(liste, suche) : []
    return (
      <tr className="border-t border-border bg-muted/30">
        <td />
        <td colSpan={8} className="px-2 py-2">
          <div className="space-y-2">
            {vorschlaege.length > 0 ? (
              <ul className="space-y-1">{vorschlaege.map((k) => kandidatKnopf(z, k.position, k.passt))}</ul>
            ) : (
              <p className="text-sm text-muted-foreground">Keine offene Bestellposition passt in Bezeichnung, Menge oder Preis. Such sie unten — oder die Position ist nicht bestellt.</p>
            )}
            <div>
              <input
                type="search"
                className="h-8 w-full max-w-md rounded-md border border-border bg-background px-2.5 text-sm"
                placeholder="Alle offenen Bestellpositionen durchsuchen — Bezeichnung, Bestellnummer, Artikelnummer"
                aria-label="Bestellposition suchen"
                value={suche}
                onChange={(e) => setSuche(e.target.value)}
                onKeyDown={(e) => e.key === 'Escape' && (suche ? setSuche('') : setWahl(null))}
                autoFocus
              />
              {suche.trim() && (
                <ul className="mt-1 space-y-1">
                  {treffer.slice(0, SUCHE_HOECHSTENS).map((p) => kandidatKnopf(z, p, worinPasst(z, p)))}
                  {treffer.length === 0 && <li className="text-sm text-muted-foreground">Nichts gefunden.</li>}
                  {treffer.length > SUCHE_HOECHSTENS && <li className="text-xs text-muted-foreground">{treffer.length - SUCHE_HOECHSTENS} weitere — tipp ein Wort dazu.</li>}
                </ul>
              )}
            </div>
          </div>
        </td>
      </tr>
    )
  }

  const kopfzeile = (titel: string, zahl: number, klasse: string, rechts?: ReactNode) => (
    <tr className="border-t border-border bg-muted/50">
      <td colSpan={9} className="px-2 py-1.5">
        <div className="flex flex-wrap items-center gap-2">
          <span className="font-medium">{titel}</span>
          <span className={`${etikett} ${klasse}`}>{zahl}</span>
          <span className="ml-auto flex flex-wrap gap-2">{rechts}</span>
        </div>
      </td>
    </tr>
  )

  const fertig = offen.length === 0 && zuPruefen.length === 0

  return (
    <Abschnitt
      titel="Abgleich mit Bestellungen"
      aktionen={
        <span className="flex flex-wrap items-center gap-2">
          <span className={`${etikett} ${offen.length ? gelb : 'bg-muted text-muted-foreground'}`}>zu klären {offen.length}</span>
          <span className={`${etikett} ${zuPruefen.length ? blau : 'bg-muted text-muted-foreground'}`}>zu bestätigen {zuPruefen.length}</span>
          <span className={`${etikett} ${gruen}`}>erledigt {erledigt.length}</span>
          <button type="button" className={`inline-flex items-center gap-1 ${knopf}`} aria-pressed={originalSichtbar} onClick={() => onOriginal(!originalSichtbar)}>
            <FileText size={14} /> {originalSichtbar ? 'Original ausblenden' : 'Original zeigen'}
          </button>
          <button type="button" className={`inline-flex items-center gap-1 ${knopf}`} onClick={onSchliessen}>
            <ArrowLeft size={14} /> Zur Erfassung
          </button>
        </span>
      }
    >
      {positionen === null ? (
        <p className="text-sm text-muted-foreground">Laden…</p>
      ) : zeilen.length === 0 ? (
        <p className="text-sm text-muted-foreground">Die Rechnung hat noch keine Positionen.</p>
      ) : (
        <div className="space-y-3">
          {liste.length === 0 && <p className="rounded-md bg-muted/50 px-3 py-2 text-sm text-muted-foreground">Zu diesem Lieferanten gibt es keine offene Bestellposition — abzugleichen ist nichts.</p>}
          {fertig && liste.length > 0 && <p className="rounded-md bg-emerald-500/10 px-3 py-2 text-sm">Alle Positionen sind geklärt.</p>}

          <div className="overflow-x-auto rounded-md border border-border">
            <table className="w-full text-sm">
              <thead className="text-left text-xs text-muted-foreground">
                <tr>
                  <th className="px-2 py-1 text-right font-normal">Pos.</th>
                  <th className="px-2 py-1 font-normal">Rechnung</th>
                  <th className="px-2 py-1 text-right font-normal">Menge</th>
                  <th className="border-r border-border px-2 py-1 text-right font-normal">Einzelpreis</th>
                  <th className="px-2 py-1 font-normal">Bestellung</th>
                  <th className="px-2 py-1 font-normal">Nr./Pos.</th>
                  <th className="px-2 py-1 text-right font-normal">offen</th>
                  <th className="px-2 py-1 text-right font-normal">vereinbart</th>
                  <th className="px-2 py-1" />
                </tr>
              </thead>
              <tbody>
                {offen.length > 0 &&
                  kopfzeile(
                    'Zu klären',
                    offen.length,
                    gelb,
                    bearbeitbar && liste.some((p) => p.offen > 0) && (
                      <button type="button" className={klein} disabled={laeuft} aria-label="Zuordnung vorschlagen" title="Sucht zu Positionen ohne Bezug die passende Bestellposition — über Artikelnummer, Bezeichnung, Menge, Einheit und Preis" onClick={() => void vorschlagen()}>
                        Zuordnung vorschlagen
                      </button>
                    ),
                  )}
                {offen.map((z) => {
                  const beste = kandidaten(z, liste, 1)[0]
                  return (
                    <Fragment key={z.id}>
                      <tr className="border-t border-border align-top">
                        {rechnungsZellen(z)}
                        <td colSpan={4} className="px-2 py-1.5 text-muted-foreground">
                          {beste ? (
                            <>
                              am nächsten: {beste.position.bestellnummer}/{beste.position.bestellzeileNr} · {beste.position.bezeichnung}
                            </>
                          ) : (
                            'keine passende Bestellposition'
                          )}
                        </td>
                        <td className="whitespace-nowrap px-2 py-1.5 text-right">
                          {bearbeitbar && (
                            <span className="inline-flex flex-wrap justify-end gap-1">
                              <button type="button" className={klein} aria-expanded={wahl === z.id} disabled={laeuft} onClick={() => (setSuche(''), setWahl(wahl === z.id ? null : z.id))}>
                                {wahl === z.id ? 'Schließen' : 'Zuordnen …'}
                              </button>
                              <button type="button" className={klein} disabled={laeuft} aria-label="Ohne Bezug" title="Die Position ist nicht bestellt. Sie gilt hier als geklärt — die Rechnung bleibt eine Abweichung und geht in die Freigabe." onClick={() => void ohneBezug(z, true)}>
                                Ohne Bezug
                              </button>
                              <button type="button" className={klein} disabled={laeuft} aria-label="Nebenkosten" title="Fracht, Zuschlag, Verpackung: Nebenkosten brauchen keine Bestellposition und laufen über die Freigrenze der Einstellungen." onClick={() => void alsArt(z, 'CHARGE')}>
                                Nebenkosten
                              </button>
                            </span>
                          )}
                        </td>
                      </tr>
                      {wahl === z.id && auswahl(z)}
                    </Fragment>
                  )
                })}

                {zuPruefen.length > 0 &&
                  kopfzeile(
                    'Zu bestätigen',
                    zuPruefen.length,
                    blau,
                    bearbeitbar && glatt.length > 0 && (
                      <button type="button" className={klein} disabled={laeuft} aria-label={`${glatt.length} ohne Abweichung bestätigen`} title="Bestätigt alle Vorschläge ohne Abweichung. Was abweicht, bestätigst du einzeln." onClick={() => void bestaetigen(glatt.map((z) => z.id))}>
                        {glatt.length} ohne Abweichung bestätigen
                      </button>
                    ),
                  )}
                {zuPruefen.map((z) => (
                  <tr key={z.id} className="border-t border-border align-top">
                    {rechnungsZellen(z)}
                    {bestellZellen(z)}
                    <td className="whitespace-nowrap px-2 py-1.5 text-right">
                      {abweichungen(z)}
                      {bearbeitbar && (
                        <span className="inline-flex gap-1">
                          <button type="button" className={klein} disabled={laeuft} onClick={() => void bestaetigen([z.id])}>
                            Bestätigen
                          </button>
                          <button type="button" className={klein} disabled={laeuft} aria-label="Lösen" title="Die Position bleibt, nur der Bezug zur Bestellung wird gelöst" onClick={() => void loesen(z)}>
                            Lösen
                          </button>
                        </span>
                      )}
                    </td>
                  </tr>
                ))}

                {erledigt.length > 0 && kopfzeile('Erledigt', erledigt.length, gruen)}
                {gruppen.map((g) => {
                  const dazu = erledigt.filter((z) => z.sourceLineId && jePosition.get(z.sourceLineId)?.bestellungId === g.bestellungId)
                  if (!dazu.length) return null
                  const auf = aufgeklappt.has(g.bestellungId)
                  const abweichend = dazu.filter((z) => !ohneAbweichung(z)).length
                  return (
                    <Fragment key={g.bestellungId}>
                      <tr className="border-t border-border">
                        <td colSpan={9} className="px-2 py-1.5">
                          <button type="button" className="flex w-full flex-wrap items-center gap-x-3 gap-y-0.5 text-left" aria-expanded={auf} onClick={() => klappe(g.bestellungId)}>
                            {auf ? <ChevronDown size={15} /> : <ChevronRight size={15} />}
                            <span className="font-medium">Bestellung {g.nummer}</span>
                            {g.datum && <span className="text-muted-foreground">vom {tagText(g.datum)}</span>}
                            {g.projekt && (
                              <span className="text-muted-foreground">
                                {terms.project} {g.projekt}
                              </span>
                            )}
                            <span className="text-muted-foreground">
                              {dazu.length} {dazu.length === 1 ? 'Position' : 'Positionen'} dieser Rechnung
                            </span>
                            {abweichend > 0 && <span className={`${etikett} ${gelb}`}>{abweichend} mit Abweichung</span>}
                            <span className={g.bleibtOffen.length ? 'text-amber-700 dark:text-amber-300' : 'text-muted-foreground'}>
                              {g.bleibtOffen.length ? `${g.bleibtOffen.length} ${g.bleibtOffen.length === 1 ? 'Bestellposition bleibt' : 'Bestellpositionen bleiben'} offen` : 'vollständig verrechnet'}
                            </span>
                          </button>
                        </td>
                      </tr>
                      {auf &&
                        dazu.map((z) => (
                          <tr key={z.id} className="border-t border-border align-top">
                            {rechnungsZellen(z)}
                            {bestellZellen(z)}
                            <td className="whitespace-nowrap px-2 py-1.5 text-right">
                              {abweichungen(z)}
                              {bearbeitbar && (
                                <button type="button" className={klein} disabled={laeuft} aria-label="Lösen" title="Die Position bleibt, nur der Bezug zur Bestellung wird gelöst" onClick={() => void loesen(z)}>
                                  Lösen
                                </button>
                              )}
                            </td>
                          </tr>
                        ))}
                      {auf &&
                        g.bleibtOffen.map((p) => (
                          <tr key={`offen-${p.quelleId}`} className="border-t border-border text-muted-foreground">
                            <td />
                            <td colSpan={3} className="border-r border-border px-2 py-1.5 italic">
                              {p.inDieserRechnung !== 0 ? 'Rest bleibt offen' : 'bleibt offen — nicht in dieser Rechnung'}
                            </td>
                            <td className="px-2 py-1.5">{p.bezeichnung}</td>
                            <td className="whitespace-nowrap px-2 py-1.5">
                              {p.bestellnummer}/{p.bestellzeileNr}
                            </td>
                            <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums">
                              {mengeText(p.offen)} {p.unitCode ?? ''}
                            </td>
                            <td className="whitespace-nowrap px-2 py-1.5 text-right tabular-nums">{preisText(vergleichspreis(p).einzelpreis)}</td>
                            <td />
                          </tr>
                        ))}
                    </Fragment>
                  )
                })}
                {verwaist.map((z) => (
                  <tr key={z.id} className="border-t border-border align-top">
                    {rechnungsZellen(z)}
                    {bestellZellen(z)}
                    <td className="whitespace-nowrap px-2 py-1.5 text-right">
                      {abweichungen(z)}
                      {bearbeitbar && (
                        <button type="button" className={klein} disabled={laeuft} onClick={() => void loesen(z)}>
                          Lösen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
                {ohneBestellung.map((z) => (
                  <tr key={z.id} className="border-t border-border align-top">
                    {rechnungsZellen(z)}
                    <td colSpan={4} className="px-2 py-1.5 text-muted-foreground">
                      {z.lineType === 'CHARGE' ? 'Nebenkosten — ohne Bestellposition' : 'ohne Bezug — nicht bestellt'}
                    </td>
                    <td className="whitespace-nowrap px-2 py-1.5 text-right">
                      {bearbeitbar && (
                        <button type="button" className={klein} disabled={laeuft} onClick={() => void (z.lineType === 'CHARGE' ? alsArt(z, 'ITEM') : ohneBezug(z, false))}>
                          Zurücknehmen
                        </button>
                      )}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </Abschnitt>
  )
}
